import { useState } from "react";
import { UploadIcon } from "lucide-react";

import { CreateCompanyFileDialog } from "./CreateCompanyFileDialog";
import { CompanyFilesTable } from "./CompanyFilesTable";
import { Column, EmptyState, ErrorText, LoadingIndicator } from "@/components";
import { Button } from "@/components/ui/button";

import { useGetCompanyFiles } from "@/hooks/queries/useGetCompanyFiles";
import { cn } from "@/lib/utils";

export const CompanyFiles = ({ className }: { className?: string }) => {
  const {
    data: companyFiles = [],
    isError: isCompanyFilesError,
    isPending: isCompanyFilesPending,
  } = useGetCompanyFiles();
  const [isCreateCompanyFileDialogOpen, setIsCreateCompanyFileDialogOpen] =
    useState(false);

  const render = () => {
    if (isCompanyFilesPending) {
      return <LoadingIndicator variant="medium" />;
    } else if (isCompanyFilesError) {
      return (
        <Column className="h-full items-center justify-center">
          <ErrorText errorMessage="An error occurred while loading company files. Please let the engineers know if they are not already on it!" />
        </Column>
      );
    } else if (companyFiles.length === 0) {
      return (
        <Column className={cn("items-end gap-6", className)}>
          <EmptyState
            description="Files uploaded for this company will appear here."
            handlePrimaryClick={() => {
              setIsCreateCompanyFileDialogOpen(true);
            }}
            Icon={UploadIcon}
            primaryButtonName="Upload file"
            primaryButtonVariant="default"
            title="Upload a file"
          />
        </Column>
      );
    }
    return (
      <Column className={cn("items-end gap-6", className)}>
        <Button
          className="rounded-full"
          onClick={() => {
            setIsCreateCompanyFileDialogOpen(true);
          }}
        >
          <UploadIcon className="h-4 w-4" />
          Upload file
        </Button>
        <CompanyFilesTable className="w-full" companyFiles={companyFiles} />
      </Column>
    );
  };

  const renderDialogs = () => (
    <CreateCompanyFileDialog
      onOpenChange={setIsCreateCompanyFileDialogOpen}
      onSuccess={() => {
        setIsCreateCompanyFileDialogOpen(false);
      }}
      open={isCreateCompanyFileDialogOpen}
    />
  );

  return (
    <>
      {render()}
      {renderDialogs()}
    </>
  );
};
